import React from 'react';
import { motion } from '@/lib/motion';
import { ArrowRight } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../data/DataProvider';
import { studioWorks as fallbackWorks } from '../data/mock_studio';
import { resolveMediaUrl, getGeneratedImage } from '../utils/mediaUrl';

// Helper to get full image URL
const getImageUrl = (path) => {
  if (!path) return getGeneratedImage('studio_architectural');
  return resolveMediaUrl(path);
};

export const StudioWorksSection = () => {
  const navigate = useNavigate();
  const { studioWorks: apiWorks } = useData();
  
  // Use API data if available, otherwise fallback
  const studioWorks = apiWorks?.length > 0 ? apiWorks : fallbackWorks;

  return (
    <section id="studio-works" className="relative py-16 md:py-20 bg-obsidian">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-16">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-10 md:mb-12"
        >
          <span className="inline-flex items-center gap-2 text-bronze text-[10px] font-body font-semibold uppercase tracking-[0.2em] mb-3">
            <span className="w-6 h-px bg-bronze" />
            WHAT WE CREATE
            <span className="w-6 h-px bg-bronze" />
          </span>
          
          <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-tight mb-3">
            STUDIO WORKS
          </h2>
          <p className="font-body text-[14px] text-white/60 max-w-lg mx-auto">
            Four pillars of artistic metal fabrication, each representing craft, design, and material expression.
          </p>
        </motion.div>

        {/* Works Grid - 2x2 */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {studioWorks.map((work, index) => (
            <motion.article
              key={work.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="group relative overflow-hidden cursor-pointer"
              onClick={() => navigate(`/studio-works/${work.slug}`)}
              data-testid={`studio-work-${work.slug}`}
            >
              {/* Image */}
              <div className="relative overflow-hidden aspect-[3/4]">
                <img loading="lazy" decoding="async"
                  src={getImageUrl(work.image)}
                  alt={work.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  width="300"
                  height="400"
                />
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/50 to-transparent opacity-80 group-hover:opacity-90 transition-opacity" />
              </div>

              {/* Content Overlay */}
              <div className="absolute inset-0 flex flex-col justify-end p-4 md:p-5">
                {/* Project Count */}
                <span className="font-display text-xl md:text-2xl font-bold text-bronze mb-1">
                  {work.projectCount}+
                </span>

                {/* Title */}
                <h3 className="font-display text-sm md:text-base font-bold text-white uppercase tracking-tight mb-1 leading-tight">
                  {work.title}
                </h3>

                {/* Subtitle — guard against studio_works rows with no subtitle */}
                <p className="font-body text-[10px] text-white/50 uppercase tracking-wider hidden md:block">
                  {(work.subtitle || '').split(' • ')[0]}
                </p>

                {/* CTA */}
                <div className="flex items-center gap-1 text-white/40 group-hover:text-bronze transition-colors mt-2">
                  <span className="font-body text-[10px] uppercase tracking-wider">
                    EXPLORE
                  </span>
                  <ArrowRight weight="bold" className="w-3 h-3 transition-transform group-hover:translate-x-1" />
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default StudioWorksSection;
